import { useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { appeler } from '../../api/client.ts';
import { useSession } from '../../composants/session.tsx';
import { Entete } from '../../composants/mise-en-page.tsx';
import {
  Carte,
  Chargement,
  DateHeure,
  Erreur,
  Etiquette,
  Vide,
  formaterDateHeure,
} from '../../composants/communs.tsx';
import { BoutonImprimer, exporterTableauPdf } from '../../composants/ui/index.ts';
import { useApercuPdf } from '../../composants/apercu-pdf.tsx';
import { FormulaireCompte } from './FormulaireCompte.tsx';
import type { Compte, Cree } from './types.ts';

/**
 * Comptes — RG-162 à RG-167.
 *
 * Le PIN provisoire n'apparaît QU'UNE FOIS. L'interface insiste, parce qu'un
 * administrateur qui referme la carte sans l'avoir noté devra le
 * réinitialiser — et le vendeur restera dehors en attendant.
 */
export function Utilisateurs(): ReactNode {
  const { ouvrir } = useApercuPdf();
  const { boutiqueId, boutiques, peut, session } = useSession();
  const client = useQueryClient();

  const [creationOuverte, setCreationOuverte] = useState(false);
  const [pinRendu, setPinRendu] = useState<Cree | null>(null);

  const { data, isPending, error } = useQuery({
    queryKey: ['utilisateurs'],
    queryFn: () => appeler<{ donnees: Compte[] }>('/utilisateurs'),
    select: (reponse) => reponse.donnees,
  });

  function rafraichir(): void {
    void client.invalidateQueries({ queryKey: ['utilisateurs'] });
  }

  const action = useMutation({
    mutationFn: ({ id, chemin }: { id: string; chemin: string }) =>
      appeler<Cree>(`/utilisateurs/${id}/${chemin}`, { methode: 'POST' }),
    onSuccess: (resultat, variables) => {
      if (variables.chemin === 'pin') {
        setPinRendu(resultat);
      }
      rafraichir();
    },
  });

  return (
    <>
      <Entete
        titre="Utilisateurs"
        sous="Un compte n’est jamais supprimé, seulement désactivé."
        actions={
          peut('utilisateur.creer') ? (
            <button
              type="button"
              onClick={() => {
                setCreationOuverte((ouvert) => !ouvert);
              }}
            >
              {creationOuverte ? 'Fermer' : 'Nouveau compte'}
            </button>
          ) : undefined
        }
      />

      <Erreur erreur={error} />
      <Erreur erreur={action.error} />

      <div className="pile">
        {pinRendu !== null && (
          <Carte titre="Code PIN provisoire">
            <p style={{ marginTop: 0 }}>
              Communiquez ce code à <strong>{pinRendu.identifiant}</strong>. Il
              n’est affiché <strong>qu’une seule fois</strong> : le serveur ne le
              conserve que haché et ne pourra pas le retrouver.
            </p>
            <div className="indicateur__valeur" style={{ letterSpacing: '0.2em' }}>
              {pinRendu.pin_provisoire}
            </div>
            <button
              type="button"
              className="bouton--discret"
              style={{ marginTop: 12 }}
              onClick={() => {
                setPinRendu(null);
              }}
            >
              J’ai noté ce code
            </button>
          </Carte>
        )}

        {creationOuverte && (
          <FormulaireCompte
            boutiqueParDefaut={boutiqueId}
            boutiques={boutiques}
            onCree={(resultat) => {
              setCreationOuverte(false);
              setPinRendu(resultat);
              rafraichir();
            }}
          />
        )}

        <Carte>
          {isPending ? (
            <Chargement />
          ) : data === undefined || data.length === 0 ? (
            <Vide texte="Aucun compte dans votre portée." />
          ) : (
            <>
              <div className="ligne" style={{ justifyContent: 'flex-end', marginBottom: 8 }}>
                <BoutonImprimer
                  onClick={() => {
                    ouvrir(() =>
                      exporterTableauPdf({
                        titre: 'Utilisateurs',
                        nomFichier: 'utilisateurs',
                        colonnes: [
                          { cle: 'identifiant', entete: 'Identifiant', rendu: () => null, texte: (c: Compte) => c.identifiant },
                          {
                            cle: 'nom',
                            entete: 'Nom',
                            rendu: () => null,
                            texte: (c: Compte) => `${c.nom}${c.prenom !== null ? ` ${c.prenom}` : ''}`,
                          },
                          { cle: 'telephone', entete: 'Téléphone', rendu: () => null, texte: (c: Compte) => c.telephone },
                          { cle: 'role', entete: 'Rôle', rendu: () => null, texte: (c: Compte) => c.role_code },
                          {
                            cle: 'boutiques',
                            entete: 'Boutique(s)',
                            rendu: () => null,
                            texte: (c: Compte) => (c.boutiques ?? []).map((b) => b.code).join(', ') || '—',
                          },
                          {
                            cle: 'etat',
                            entete: 'État',
                            rendu: () => null,
                            texte: (c: Compte) => (!c.actif ? 'Désactivé' : c.verrouille ? 'Verrouillé' : 'Actif'),
                          },
                          {
                            cle: 'actif_depuis',
                            entete: 'État depuis',
                            rendu: () => null,
                            texte: (c: Compte) => formaterDateHeure(c.actif_depuis),
                          },
                        ],
                        lignes: data,
                      }),
                    );
                  }}
                />
              </div>
              <div className="tableau-defilant">
              <table>
                <thead>
                  <tr>
                    <th>Identifiant</th>
                    <th>Nom</th>
                    <th>Téléphone</th>
                    <th>Rôle</th>
                    <th>Boutique(s)</th>
                    <th>État</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {data.map((compte) => (
                    <LigneCompte
                      key={compte.id}
                      compte={compte}
                      soiMeme={compte.id === session?.utilisateur.id}
                      peut={peut}
                      onAction={(chemin) => {
                        action.mutate({ id: compte.id, chemin });
                      }}
                    />
                  ))}
                </tbody>
              </table>
              </div>
            </>
          )}
        </Carte>
      </div>
    </>
  );
}

function LigneCompte({
  compte,
  soiMeme,
  peut,
  onAction,
}: {
  compte: Compte;
  soiMeme: boolean;
  peut: (permission: string) => boolean;
  onAction: (chemin: string) => void;
}): ReactNode {
  return (
    <tr>
      <td className="mono">{compte.identifiant}</td>
      <td>
        {compte.nom}
        {compte.prenom !== null && ` ${compte.prenom}`}
      </td>
      <td className="mono">{compte.telephone}</td>
      <td className="mono">{compte.role_code}</td>
      <td>{(compte.boutiques ?? []).map((b) => b.code).join(', ') || '—'}</td>
      <td>
        {!compte.actif ? (
          <Etiquette ton="rouge" texte="désactivé" />
        ) : compte.verrouille ? (
          <Etiquette ton="ambre" texte="verrouillé" />
        ) : (
          <Etiquette ton="vert" texte="actif" />
        )}
        <div className="entete__sous">
          depuis <DateHeure valeur={compte.actif_depuis} />
        </div>
      </td>
      <td>
        <div className="ligne">
          {peut('utilisateur.pin.reinitialiser') && (
            <button
              type="button"
              className="bouton--discret"
              onClick={() => {
                onAction('pin');
              }}
            >
              Réinitialiser le PIN
            </button>
          )}
          {compte.verrouille && peut('utilisateur.deverrouiller') && (
            <button
              type="button"
              className="bouton--discret"
              onClick={() => {
                onAction('deverrouillage');
              }}
            >
              Déverrouiller
            </button>
          )}
          {/* Nul ne se désactive soi-même : le serveur refuse (RG-162), et
              masquer le bouton évite de proposer une impasse. */}
          {peut('utilisateur.desactiver') && !soiMeme && (
            <button
              type="button"
              className="bouton--discret"
              onClick={() => {
                onAction(!compte.actif ? 'reactivation' : 'desactivation');
              }}
            >
              {!compte.actif ? 'Réactiver' : 'Désactiver'}
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}
